import { defineStore } from 'pinia';
import { collection, deleteDoc, doc, getDocs, runTransaction, setDoc } from 'firebase/firestore';
import {
    collectEntityTagSuggestions,
    ENTITY_TAG_DEFINITIONS_DOCUMENT_ID,
    entityTagDocumentId,
    normalizeEntityTagColors,
    normalizeEntityTags,
    resolveEntityTagColor,
    tagKey,
    type EntityTagColors,
    type TaggableEntityType,
} from '~/lib/entityTags';

type EntityTagRecord = {
    id: string;
    entityType: TaggableEntityType;
    entityId: string;
    tags: string[];
    createdAt?: unknown;
    updatedAt?: unknown;
};

export const useEntityTagsStore = defineStore('entityTags', {
    state: () => ({
        records: [] as EntityTagRecord[],
        tagColors: {} as EntityTagColors,
        loadedForTenantId: null as string | null,
        loading: false,
        error: null as string | null,
    }),

    getters: {
        tagsFor: state => (entityType: TaggableEntityType, entityId: string): string[] => (
            state.records.find(record => record.entityType === entityType && record.entityId === entityId)?.tags || []
        ),
        allTags: state => collectEntityTagSuggestions(state.records.map(record => record.tags)),
        colorFor: state => (tag: string): string => resolveEntityTagColor(tag, state.tagColors),
        tagsForType: state => (entityType: TaggableEntityType): string[] => (
            collectEntityTagSuggestions(
                state.records.filter(record => record.entityType === entityType).map(record => record.tags),
            )
        ),
        hasTag: state => (entityType: TaggableEntityType, entityId: string, tag: string): boolean => {
            const selectedKey = tagKey(tag);
            const tags = state.records.find(record => record.entityType === entityType && record.entityId === entityId)?.tags || [];
            return tags.some(value => tagKey(value) === selectedKey);
        },
    },

    actions: {
        _tenantId(): string {
            const tenantId = useTenantStore().currentTenantId;
            if (!tenantId) throw new Error('No tenant selected. Please log in.');
            return tenantId;
        },

        async fetchEntityTags(force = false): Promise<void> {
            const tenantId = this._tenantId();
            if (!force && this.loadedForTenantId === tenantId) return;
            if (this.loading) return;

            const { $db } = useNuxtApp();
            this.loading = true;
            this.error = null;

            try {
                const snapshot = await getDocs(collection($db, `tenants/${tenantId}/entityTags`));
                const definitions = snapshot.docs.find(tagDoc => tagDoc.id === ENTITY_TAG_DEFINITIONS_DOCUMENT_ID);
                this.tagColors = normalizeEntityTagColors(definitions?.data()?.colors);
                this.records = snapshot.docs
                    .filter(tagDoc => tagDoc.id !== ENTITY_TAG_DEFINITIONS_DOCUMENT_ID)
                    .map(tagDoc => ({ id: tagDoc.id, ...tagDoc.data() } as EntityTagRecord))
                    .filter(record => ['project', 'task'].includes(record.entityType) && Boolean(record.entityId))
                    .map(record => ({ ...record, tags: normalizeEntityTags(record.tags) }));
                this.loadedForTenantId = tenantId;
            } catch (error) {
                this.error = error instanceof Error ? error.message : 'Tags could not be loaded.';
                this.records = [];
                this.tagColors = {};
                this.loadedForTenantId = tenantId;
            } finally {
                this.loading = false;
            }
        },

        async setEntityTags(entityType: TaggableEntityType, entityId: string, values: string[]): Promise<string[]> {
            const tenantId = this._tenantId();
            const { $db } = useNuxtApp();
            const id = entityTagDocumentId(entityType, entityId);
            const tags = normalizeEntityTags(values);
            const recordIndex = this.records.findIndex(record => record.id === id);
            const previousRecord = recordIndex >= 0 ? this.records[recordIndex] : null;
            const tagRef = doc($db, `tenants/${tenantId}/entityTags/${id}`);

            if (tags.length === 0) {
                if (recordIndex >= 0) this.records.splice(recordIndex, 1);
                try {
                    await deleteDoc(tagRef);
                } catch (error) {
                    if (previousRecord) this.records.push(previousRecord);
                    throw error;
                }
                return [];
            }

            const nextRecord: EntityTagRecord = {
                id,
                entityType,
                entityId,
                tags,
                createdAt: previousRecord?.createdAt || new Date(),
                updatedAt: new Date(),
            };
            if (recordIndex >= 0) this.records.splice(recordIndex, 1, nextRecord);
            else this.records.push(nextRecord);

            try {
                await setDoc(tagRef, {
                    entityType,
                    entityId,
                    tags,
                    createdAt: previousRecord?.createdAt || new Date(),
                    updatedAt: new Date(),
                }, { merge: true });
            } catch (error) {
                const nextIndex = this.records.findIndex(record => record.id === id);
                if (nextIndex >= 0) this.records.splice(nextIndex, 1);
                if (previousRecord) this.records.push(previousRecord);
                throw error;
            }

            return tags;
        },

        async setTagColors(values: EntityTagColors): Promise<EntityTagColors> {
            const tenantId = this._tenantId();
            const updates = normalizeEntityTagColors(values);
            if (!Object.keys(updates).length) return this.tagColors;

            const { $db } = useNuxtApp();
            const definitionsRef = doc($db, `tenants/${tenantId}/entityTags/${ENTITY_TAG_DEFINITIONS_DOCUMENT_ID}`);
            let mergedColors = { ...this.tagColors, ...updates };

            await runTransaction($db, async transaction => {
                const snapshot = await transaction.get(definitionsRef);
                const currentColors = normalizeEntityTagColors(snapshot.data()?.colors);
                mergedColors = normalizeEntityTagColors({ ...currentColors, ...updates });
                transaction.set(definitionsRef, {
                    colors: mergedColors,
                    updatedAt: new Date(),
                }, { merge: true });
            });

            this.tagColors = mergedColors;
            return this.tagColors;
        },

        async removeEntityTags(entityType: TaggableEntityType, entityId: string): Promise<void> {
            await this.setEntityTags(entityType, entityId, []);
        },
    },
});
